import _ from 'lodash';
import Jax from '../../components/jax/jax';
import { onDomChanges, onDomReady } from '../../components/dynamic/observer';

const BREAKPOINTS = { MEDIUM: 768, LARGE: 1260 };
const EVENTS = { MUTATED: 'DOMContentMutated', CATALOG: 'CatalogReplaced' };

class Filter {
  constructor(form) {
    this.form = form;
    this.replaceOnMobile = false;
    this.debounceRequest = _.debounce(() => this.request(), 1000);
    this.initElements();
    this.init();
  }

  initElements() {
    // Inputs
    this.checkedInputs = [...this.form.querySelectorAll('input[type="checkbox"], input[type="radio"]')];
    this.rangeInputs = [...this.form.querySelectorAll('.range-input input')];
    // Buttons / links
    this.buttons = {
      opener: this.form.querySelector('[data-filter-opener]'),
      closer: this.form.querySelector('[data-filter-closer]'),
      submit: this.form.querySelector('[data-submit-button]'),
      reset: this.form.querySelector('[data-button-reset]'),
      lastSearch: this.form.querySelector('[data-last-search-button]'),
      mobileOpeners: [...document.querySelectorAll('[data-filter-opener-mob]')],
      viewLinks: [...this.form.querySelectorAll('[data-change-view]')],
    };
  }

  init() {
    this.bindInputs();
    this.bindControls();
    this.bindChains();
  }

  // =========== INPUTS ===========

  bindInputs() {
    this.checkedInputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        this.debounceRequest();
        if (e.target.closest('[data-bottom-fields]')) this.replaceOnMobile = true;
      });
    });
    this.rangeInputs.forEach(input => input.addEventListener('change', this.debounceRequest));
  }

  // =========== CHAINS (проекты → адреса → секции) ===========

  bindChains() {
    const projectInputs = [...this.form.querySelectorAll('[data-field-project] input[type="checkbox"]')];
    const addressInputs = [...this.form.querySelectorAll('[data-field-address]  input[type="checkbox"]')];

    if (projectInputs.length && addressInputs.length) {
      projectInputs.forEach(pi => pi.addEventListener('change', () => this.constructor.onProjectChange(projectInputs, addressInputs)));
      const sectionField = this.form.querySelector('[data-field-section]');
      const sectionInputs = sectionField ? [...sectionField.querySelectorAll('input[type="checkbox"]')] : [];
      if (sectionField && sectionInputs.length) {
        addressInputs.forEach(ai => ai.addEventListener('change', () => this.constructor.onAddressChange(addressInputs, sectionInputs, sectionField)));
      }
    }
  }

  static onProjectChange(projectInputs, addressInputs) {
    const selected = projectInputs
      .filter(i => i.checked && i.value !== '0')
      .map(i => +i.value);

    // сброс «Все проекты»
    const zero = projectInputs.find(i => i.value === '0');
    if (selected.length === 0 && !zero.checked) {
      zero.checked = true;
      zero.dispatchEvent(new Event('change'));
    }

    // фильтрация адресов
    addressInputs.forEach((ai) => {
      const pid = +ai.dataset.projectId || 0;
      ai.parentElement.classList.toggle('_hidden', selected.length > 0 && pid && !selected.includes(pid));
    });
  }

  static onAddressChange(addressInputs, sectionInputs, sectionField) {
    // сброс секций
    sectionInputs.forEach((si) => {
      si.checked = false;
      si.dispatchEvent(new Event('change'));
    });
    const sections = new Set();

    addressInputs
      .filter(i => i.checked && i.value !== '0')
      .forEach(i => i.dataset.sections.split(',').map(Number).forEach(n => sections.add(n)));

    sectionField.classList.toggle('_visible', sections.size > 0);
    sectionInputs.forEach((si) => {
      const id = +si.value;
      si.parentElement.classList.toggle('_hidden', sections.size > 0 && !sections.has(id));
    });
  }

  // =========== CONTROLS (открытие/закрытие/submit/reset/view) ===========

  bindControls() {
    const b = this.buttons;
    if (b.opener) b.opener.addEventListener('click', e => this.toggleFilter(true, e));
    if (b.closer) b.closer.addEventListener('click', e => this.toggleFilter(false, e));
    b.mobileOpeners.forEach(btn => btn.addEventListener('click', e => this.toggleFilter(true, e)));


    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.request();
      });
    }

    if (b.submit) {
      b.submit.addEventListener('click', (e) => {
        e.preventDefault();
        this.replaceOnMobile = true;
        this.request();
        this.toggleFilter(false, e);
      });
    }

    if (b.reset) {
      b.reset.addEventListener('click', (e) => {
        e.preventDefault();
        this.resetAll();
      });
    }

    if (b.lastSearch) {
      b.lastSearch.addEventListener('click', (e) => {
        e.preventDefault();
        b.lastSearch.remove();
        this.lastSearchRequest();
      });
    }

    b.viewLinks.forEach(link => link.addEventListener('click', (e) => {
      e.preventDefault();
      this.switchView(link);
    }));
  }

  toggleFilter(open, e) {
    e.preventDefault();
    this.form.classList.toggle('_open', open);
    this.constructor.dispatchEvents();
  }

  resetAll() {
    // чекбоксы и радио
    this.checkedInputs.forEach((i) => {
      i.checked = (i.value === '0');
      i.dispatchEvent(new Event('change'));
    });
    // диапазоны
    this.rangeInputs.forEach((i) => {
      const parent = i.closest('[data-range-input]');
      if (parent) {
        i.value = i.dataset.rangeFromInput !== undefined
          ? parent.dataset.min
          : parent.dataset.max;
      }
      i.dispatchEvent(new Event('hardSet'));
    });
  }

  switchView(activeLink) {
    const other = this.buttons.viewLinks.find(l => l !== activeLink);
    const catalog = document.querySelector('[data-catalog-content]');
    if (!other || !catalog) return;
    catalog.classList.replace(
      `_view-mode_${other.dataset.changeView}`,
      `_view-mode_${activeLink.dataset.changeView}`,
    );
    activeLink.classList.add('_active');
    other.classList.remove('_active');
  }

  // =========== AJAX ===========

  request() {
    // удаляем кнопку «Последний поиск»
    if (this.buttons.lastSearch) this.buttons.lastSearch.remove();

    // собираем данные формы
    const formData = new FormData(this.form);

    // формируем строку запроса и обновляем URL без перезагрузки
    const params = new URLSearchParams(formData).toString();
    const newUrl = `${window.location.pathname}?${params}`;
    window.history.replaceState(null, '', newUrl);

    // отправляем AJAX-запрос
    new Jax(this.form.action, this.form.method)
      .send(formData)
      .then(html => this.handleResponse(html));
  }

  lastSearchRequest() {
    new Jax(`${this.form.action}?last_layout_search=true`, this.form.method)
      .send(new FormData(this.form))
      .then((html) => {
        this.handleResponse(html);
        this.replaceFields(html);
        this.init(); // перебинд после замены полей
      });
  }

  handleResponse(html) {
    const dom = this.constructor.createDOM(html);
    this.updateTotals(dom);
    if (window.innerWidth >= BREAKPOINTS.MEDIUM || this.replaceOnMobile) {
      this.constructor.updateCatalog(dom);
      this.replaceOnMobile = false;
    }
    this.constructor.dispatchEvents();
  }

  replaceFields(html) {
    const dom = this.constructor.createDOM(html);
    ['[data-top-fields]', '[data-bottom-fields]', '[data-tags]'].forEach((sel) => {
      const src = dom.querySelector(sel);
      const dst = this.form.querySelector(sel);
      if (src && dst) dst.innerHTML = src.innerHTML;
    });
    this.constructor.dispatchEvents();
  }

  updateTotals(dom) {
    const count = dom.querySelector('[data-total-count]');
    const text = dom.querySelector('[data-total-text]');
    if (count) this.form.querySelectorAll('[data-total-count]').forEach((el) => { el.innerHTML = count.innerHTML; });
    if (text) this.form.querySelectorAll('[data-total-text]').forEach((el) => { el.innerHTML = text.innerHTML; });
  }

  static updateCatalog(dom) {
    const src = dom.querySelector('[data-catalog-content]');
    const dst = document.querySelector('[data-catalog-content]');
    if (src && dst) dst.innerHTML = src.innerHTML;
  }

  // =========== UTILS ===========

  static createDOM(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div;
  }

  static dispatchEvents() {
    document.dispatchEvent(new Event(EVENTS.MUTATED));
    document.dispatchEvent(new Event(EVENTS.CATALOG));
  }
}

// Инициализация
const init = () => {
  const form = document.querySelector('[data-layout-filter]:not([data-initialized])');
  if (form) {
    form.dataset.initialized = 'true';
    form.filter = new Filter(form);
  }
};

onDomReady(init);
onDomChanges(init);